import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { build, buildWithParams, pool } from '@fitogest/reactive-form-builder/utils';
import {
    ReactiveFormLifecycleEvent,
    ReactiveFormStableValueChange,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { StorageManager } from '../../../../../common/storage-manager.class';
import { BaseForm } from '../../../../base-form';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { TareasApiService } from '../../../../../service/api/tareas-api.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { TrabajadoresApiService } from '../../../../../service/api/trabajadores-api.service';
import { RequestHandler } from '../../../../../service/OffService/request-handler';
import { TareaEdificiosModel } from '../../../../../models/tareas/tarea-edificios.model';
import { TareasEdificiosFormDefinition } from './form-control/form-definition';

@Component({
    selector: 'app-tareas-edificios-form',
    standalone: false,
    templateUrl: './tareas-edificios-form.component.html',
    styleUrls: ['./tareas-edificios-form.component.scss'],
})
export class TareasEdificiosFormComponent extends BaseForm<TareaEdificiosModel> implements OnInit {
    public form!: TareasEdificiosFormDefinition;
    public isReactiveFormReady = false;
    public reactiveFormLoadingError = '';
    public loadingData = true;
    public getRequest!: RequestHandler<TareaEdificiosModel[]>;
    public reactiveFormRecordId = '';
    public override model: TareaEdificiosModel = {};
    public initialPreviousData: TareaEdificiosModel | null = null;

    private lastReloadedClientDropdownId = '';
    private isReloadingClientDropdowns = false;

    constructor(
        public override route: ActivatedRoute,
        public override dashboard: DashboardService,
        public fincasApi: FincasApiService,
        public clientesApi: ClientesApiService,
        public trabajadoresApi: TrabajadoresApiService,
        public tareasApi: TareasApiService,
        public override router: Router,
        private readonly cdr: ChangeDetectorRef,
    ) {
        super(
            route,
            router,
            dashboard,
            tareasApi.edificios.PUT,
            tareasApi.edificios.POST,
            'edificios',
            'Actualizar Tarea Edificios',
            'Realizar Tarea Edificios',
            'Duplicar Tarea Edificios',
        );
        this.getRequest = tareasApi.edificios.GET;
    }

    public ngOnInit(): void {
        this.dashboard.clientChanges$.subscribe(() => {
            this.fincasApi.fincasGET.unsuscribe();
            
        });

        this.softInit(this.getType());
        void this.setupForm();
    }

    public stableValueChanges(event: ReactiveFormStableValueChange<TareaEdificiosModel>): void {
        const stableValue = this.captureStableValueChange(event.value as TareaEdificiosModel);

        this.form.model = this.toDomainModel({
            ...this.form.model,
            ...stableValue,
        });
        this.syncModelFromForm();

        if (event.origin !== 'user') {
            return;
        }

        this.handleUserValueChange(event.value, event.changedField);
    }

    public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<TareaEdificiosModel>): void {
        if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged') {
            return;
        }

        const stableValue = this.captureStableValueChange(event.value as TareaEdificiosModel);

        this.form.model = this.toDomainModel({
            ...this.form.model,
            ...stableValue,
        });
        this.syncModelFromForm();
        this.syncCalculatedHours(String(event.changedField ?? ''));
    }

    public onAfterSend(): void {
        StorageManager.saveSessionDataDonJardin(
            this.form.model.id_finca ?? '',
            this.form.model.hora_inicio ?? '',
            this.form.model.hora_fin?.toString() ?? '',
        );
    }

    private async setupForm(): Promise<void> {
        try {
            this.loadingData = true;
            this.isReactiveFormReady = false;
            this.reactiveFormLoadingError = '';
            this.reactiveFormRecordId = this.resolveReactiveFormRecordId();
            const initialModel = this.initializeHostModel(this.toDomainModel(this.getModel() ?? {}));

            this.initialPreviousData = { ...initialModel };
            this.form = new TareasEdificiosFormDefinition(this.getType(), initialModel);
            this.form.model = this.toDomainModel(initialModel);

            await this.initFormData();
            this.syncModelFromForm();
            this.markReactiveFormReady();
        } catch (error: unknown) {
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.reactiveFormLoadingError = this.getReadableInitialisationError(error);
            this.cdr.markForCheck();
            console.error('No se ha podido preparar el formulario de edificios.', error);
        }
    }

    private async initFormData(): Promise<boolean> {
        this.loadingData = true;
        this.resetExternalDropdowns();

        const buildsRequest = this.getDropdownBuildsForConfiguredCatalogs();

        return pool(buildsRequest, () => this.refreshReactiveDropdownMappings())
            .then(() => {
                this.loadingData = false;
                this.refreshReactiveDropdownMappings();

                return true;
            })
            .catch((error: unknown) => {
                this.loadingData = false;
                this.refreshReactiveDropdownMappings();
                console.error('Error loading MTO reactive form data.', error);
                throw error;
            });
    }

    private getDropdownBuildsForConfiguredCatalogs(): ReturnType<typeof build>[] {
        const configuredCatalogs = this.getConfiguredDropdownCatalogs();
        const buildsRequest: ReturnType<typeof build>[] = [];

        if (configuredCatalogs.has('id_finca')) {
            buildsRequest.push(build(this.fincasApi.fincasGET, this.form.lists.fincas, 'nombre', true));
        }

        if (configuredCatalogs.has('id_cliente')) {
            buildsRequest.push(build(this.clientesApi.clientesGET, this.form.lists.clientes, { or: ['razon_social', 'nombre'] }));
        }

        if (configuredCatalogs.has('ids_trabajadores')) {
            buildsRequest.push(build(
                this.trabajadoresApi.trabajadoresGET,
                this.form.lists.trabajadores,
                (it: { nombre?: string; apellidos?: string }) => `${it.nombre ?? ''} ${it.apellidos ?? ''}`.trim(),
                true,
                true,
            ));
        }

        return buildsRequest;
    }

    private getConfiguredDropdownCatalogs(): Set<string> {
        const dropdownCatalogs = this.form.formFields.dropdownCatalogs;

        return new Set([
            ...(dropdownCatalogs?.critical ?? []),
            ...(dropdownCatalogs?.deferred ?? []),
            ...(dropdownCatalogs?.lazy ?? []),
        ]);
    }

    private handleUserValueChange(
        formModel: ReactiveFormValue<TareaEdificiosModel>,
        lastChangedFieldName: keyof TareaEdificiosModel | string | null | undefined,
    ): void {
        const changedFieldName = String(lastChangedFieldName ?? '');

        this.form.model = this.toDomainModel({
            ...this.form.model,
            ...formModel,
        });
        this.syncModelFromForm();
        this.syncCalculatedHours(changedFieldName);

        if (changedFieldName === 'mantenimiento') {
            this.form.updateMantenimientoLabel();
            this.refreshReactiveDropdownMappings();
        }

        if (changedFieldName === 'id_cliente') {
            this.onClientChanged();
        }

        if (changedFieldName === 'id_finca' || changedFieldName === 'id_sector') {
            this.refreshReactiveDropdownMappings();
        }
    }

    private syncCalculatedHours(changedFieldName: string): void {
        const updates = this.form.getHourUpdatesForChangedField(changedFieldName);
        const updateEntries = Object.entries(updates);

        if (updateEntries.length === 0) {
            return;
        }

        this.form.model = this.toDomainModel({
            ...this.form.model,
            ...updates,
        });
        this.syncModelFromForm();

        this.formBuilderRef?.updateForm(updates as Partial<TareaEdificiosModel>, true);
    }

    private onClientChanged(): void {
        const selectedClientId = this.getReactiveValueId(this.form.model.id_cliente);

        if (!selectedClientId || this.isReloadingClientDropdowns || selectedClientId === this.lastReloadedClientDropdownId) {
            return;
        }

        this.isReloadingClientDropdowns = true;
        this.lastReloadedClientDropdownId = selectedClientId;
        this.form.model = this.toDomainModel({
            ...this.form.model,
            id_finca: null,
        });
        this.syncModelFromForm();

        pool([
            buildWithParams(this.fincasApi.fincasGET, { id_cliente: selectedClientId }, this.form.lists.fincas, 'nombre', true),
            
        ], () => this.refreshReactiveDropdownMappings())
            .then(() => {
                this.form.lists.fincas.filtered = this.form.lists.fincas.values;
                this.isReloadingClientDropdowns = false;
                this.refreshReactiveDropdownMappings();

                return true;
            })
            .catch((error: unknown) => {
                this.isReloadingClientDropdowns = false;
                this.refreshReactiveDropdownMappings();
                console.error('Error reloading MTO client dropdowns.', error);
            });
    }

    private markReactiveFormReady(): void {
        this.loadingData = false;
        this.isReactiveFormReady = true;
        this.cdr.detectChanges();
        this.finalizeExternalDropdowns();
        this.refreshReactiveDropdownMappings();
        this.cdr.markForCheck();
    }

    private syncModelFromForm(): void {
        const normalizedModel = this.toDomainModel(this.form.model);

        this.form.model = normalizedModel;
        this.updateHostModel(normalizedModel);
    }

    private toDomainModel(sourceModel: Partial<TareaEdificiosModel> | ReactiveFormValue<TareaEdificiosModel>): TareaEdificiosModel {
        const normalizedModel: Record<string, unknown> = {
            ...(sourceModel as unknown as Record<string, unknown>),
        };

        return normalizedModel as unknown as TareaEdificiosModel;
    }

    private getReadableInitialisationError(error: unknown): string {
        if (error instanceof Error && error.message.trim().length > 0) {
            return error.message;
        }

        if (typeof error === 'string' && error.trim().length > 0) {
            return error;
        }

        return 'No se ha podido preparar el formulario de edificios.';
    }

    private refreshReactiveDropdownMappings(): void {
        this.formBuilderRef?.requestExternalDropdownMappingsRefresh();
    }

    private getReactiveValueId(value: unknown): string {
        if (typeof value === 'object' && value !== null && 'id' in value) {
            return String((value as { id?: unknown }).id ?? '');
        }

        return String(value ?? '');
    }
}
